import { useMemo, useState, useEffect } from 'react';
import { Modal, Button, Table, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.service';
import {
  AplicarDecisoesComparecimentoCorregedoria,
  ComparecimentoDecisaoAcao,
} from '@/modules/reports/application/relatorios-corregedoria.service';

const meses: Record<string, string> = {
  '01': 'Jan',
  '02': 'Fev',
  '03': 'Mar',
  '04': 'Abr',
  '05': 'Mai',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Ago',
  '09': 'Set',
  '10': 'Out',
  '11': 'Nov',
  '12': 'Dez',
};

type DiaPendenteRow = {
  dia: number;
  mes: string;
  ano: number;
  tipo: 'remoto' | 'ausente';
  key: string;
  justificativaTexto?: string;
};

function extrairDiasPendentes(relatorio: RelatorioAtividadeDto): DiaPendenteRow[] {
  const diasStatus = relatorio.dados?.comparecimento?.diasStatus as
    | Array<{
        dia: number;
        mes?: string;
        ano?: number;
        status?: string;
        remotoValidado?: boolean;
        ausenteAceitoCorregedoria?: boolean;
        justificativaTexto?: string;
      }>
    | undefined;
  if (!Array.isArray(diasStatus)) return [];
  const mesRel = String(relatorio.mes).padStart(2, '0');
  const anoRel = relatorio.ano;
  const out: DiaPendenteRow[] = [];
  for (const d of diasStatus) {
    const mes = d.mes != null ? String(d.mes).padStart(2, '0') : mesRel;
    const ano = d.ano ?? anoRel;
    const key = `${d.dia}-${mes}-${ano}`;
    if (d.status === 'remoto' && d.remotoValidado !== true) {
      out.push({
        dia: d.dia,
        mes,
        ano,
        tipo: 'remoto',
        key,
        justificativaTexto: d.justificativaTexto,
      });
    }
    if (d.status === 'ausente' && d.ausenteAceitoCorregedoria !== true) {
      out.push({
        dia: d.dia,
        mes,
        ano,
        tipo: 'ausente',
        key,
        justificativaTexto: d.justificativaTexto,
      });
    }
  }
  return out.sort((a, b) => a.ano - b.ano || a.mes.localeCompare(b.mes) || a.dia - b.dia);
}

interface ModalValidarRemotosProps {
  show: boolean;
  relatorio: RelatorioAtividadeDto | null;
  onHide: () => void;
  /** Chamado quando não há mais dias pendentes e o usuário confirma o aval final. */
  onConfirm: () => void;
  loading?: boolean;
}

export default function ModalValidarRemotos({
  show,
  relatorio,
  onHide,
  onConfirm,
  loading = false,
}: ModalValidarRemotosProps) {
  const [local, setLocal] = useState<RelatorioAtividadeDto | null>(null);
  const [rowLoading, setRowLoading] = useState<string | null>(null);

  useEffect(() => {
    if (show && relatorio) {
      setLocal(relatorio);
    }
    if (!show) {
      setLocal(null);
      setRowLoading(null);
    }
  }, [show, relatorio]);

  const pendentes = useMemo(() => (local ? extrairDiasPendentes(local) : []), [local]);

  const aplicar = async (row: DiaPendenteRow, acao: ComparecimentoDecisaoAcao) => {
    if (!local?.id) return;
    setRowLoading(row.key);
    try {
      const atualizado = await AplicarDecisoesComparecimentoCorregedoria(local.id, [
        { dia: row.dia, mes: row.mes, ano: row.ano, acao },
      ]);
      setLocal(atualizado);
      toast.success('Decisão registrada.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Erro ao aplicar decisão.');
    } finally {
      setRowLoading(null);
    }
  };

  const handleConfirmAval = () => {
    if (pendentes.length > 0) {
      toast.warning('É necessário decidir sobre todos os dias de remoto ou ausência antes do aval final.');
      return;
    }
    onConfirm();
  };

  const handleHide = () => {
    setRowLoading(null);
    onHide();
  };

  if (!relatorio) return null;

  return (
    <Modal show={show} onHide={handleHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Comparecimento — decisão da Corregedoria</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Para cada dia <strong>remoto</strong>, valide como presença ou anule a marcação. Para cada dia{' '}
          <strong>ausente</strong>, aceite a justificativa ou anule para o defensor corrigir. Depois, confirme o aval
          final do relatório.
        </p>
        {pendentes.length === 0 ? (
          <p className="mb-0 text-success">
            <i className="bi bi-check-circle me-2" aria-hidden />
            Não há dias pendentes. Você pode registrar o aval final.
          </p>
        ) : (
          <div className="table-responsive">
            <Table bordered hover size="sm" className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Justificativa</th>
                  <th style={{ minWidth: 220 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pendentes.map((row) => {
                  const busy = rowLoading === row.key;
                  const labelData = `${row.dia} ${meses[row.mes] || row.mes}/${row.ano}`;
                  return (
                    <tr key={`${row.tipo}-${row.key}`}>
                      <td>{labelData}</td>
                      <td>{row.tipo === 'remoto' ? 'Remoto' : 'Ausente'}</td>
                      <td>
                        <small className="text-muted">{row.justificativaTexto?.trim() || '—'}</small>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {row.tipo === 'remoto' ? (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                disabled={busy || loading}
                                onClick={() => aplicar(row, 'remoto_validar')}
                              >
                                {busy ? <Spinner animation="border" size="sm" /> : 'Validar presença'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                disabled={busy || loading}
                                onClick={() => aplicar(row, 'remoto_rejeitar')}
                              >
                                Anular dia
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                disabled={busy || loading}
                                onClick={() => aplicar(row, 'ausencia_aceitar')}
                              >
                                {busy ? <Spinner animation="border" size="sm" /> : 'Aceitar ausência'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                disabled={busy || loading}
                                onClick={() => aplicar(row, 'ausencia_rejeitar')}
                              >
                                Anular dia
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={loading}>
          Fechar
        </Button>
        <Button variant="primary" onClick={handleConfirmAval} disabled={loading || pendentes.length > 0}>
          {loading ? 'Processando...' : 'Confirmar aval final'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
